/**
 * Authentication Context
 *
 * Provides authentication state and methods throughout the app.
 * Handles Firebase Auth state changes and user/org data fetching.
 *
 * @module contexts/AuthContext
 */

import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  type ReactNode,
} from 'react';
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signInWithPopup,
  signOut as firebaseSignOut,
  GoogleAuthProvider,
  GithubAuthProvider,
  type User as FirebaseUser,
} from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import { doc, getDoc, onSnapshot } from 'firebase/firestore';
import { auth, db, functions } from '@/lib/firebase';
import type { User, Organization } from '@/lib/firebase-types';
import {
  setSentryUser,
  setSentryOrganization,
  clearSentryUser,
  captureException,
} from '@/lib/sentry';

// =============================================================================
// Helper Functions
// =============================================================================

/**
 * Check if an email is in the super admins list
 * Requires both: email in _config/superAdmins AND @cleverbear.com domain
 */
async function checkIsSuperAdmin(email: string | null | undefined): Promise<boolean> {
  if (!email || !email.endsWith('@cleverbear.com')) {
    return false;
  }

  try {
    const configDoc = await getDoc(doc(db, '_config', 'superAdmins'));
    if (!configDoc.exists()) {
      return false;
    }

    const configData = configDoc.data();
    const superAdminEmails: string[] = configData?.emails || [];
    const normalizedEmails = superAdminEmails.map(e => e.toLowerCase());

    return normalizedEmails.includes(email.toLowerCase());
  } catch (err) {
    console.error('[SuperAdmin] Error checking super admin status:', err);
    return false;
  }
}

// =============================================================================
// Types
// =============================================================================

interface AuthState {
  /** Firebase auth user */
  firebaseUser: FirebaseUser | null;
  /** User document from Firestore */
  user: User | null;
  /** Current organization */
  organization: Organization | null;
  /** Whether auth state is being determined */
  loading: boolean;
  /** Auth error if any */
  error: string | null;
  /** Whether user is a super admin (requires email in _config/superAdmins + @cleverbear.com domain) */
  isSuperAdmin: boolean;
}

interface AuthContextValue extends AuthState {
  /** Sign in with email and password */
  signIn: (email: string, password: string) => Promise<void>;
  /** Sign up with email and password */
  signUp: (email: string, password: string) => Promise<void>;
  /** Sign in with Google */
  signInWithGoogle: () => Promise<void>;
  /** Sign in with GitHub */
  signInWithGithub: () => Promise<void>;
  /** Sign out */
  signOut: () => Promise<void>;
  /** Send password reset email */
  resetPassword: (email: string) => Promise<void>;
  /** Switch to a different organization */
  switchOrganization: (orgId: string) => Promise<void>;
  /** Refresh current organization data */
  refreshOrganization: () => Promise<void>;
  /** Refresh Firebase auth state (e.g., after server-side email verification) */
  refreshAuth: () => Promise<void>;
  /** Clear any auth errors */
  clearError: () => void;
}

// =============================================================================
// Context
// =============================================================================

const AuthContext = createContext<AuthContextValue | null>(null);

// =============================================================================
// Provider
// =============================================================================

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [state, setState] = useState<AuthState>({
    firebaseUser: null,
    user: null,
    organization: null,
    loading: true,
    error: null,
    isSuperAdmin: false,
  });

  // ---------------------------------------------------------------------------
  // Auth State Listener
  // ---------------------------------------------------------------------------

  useEffect(() => {
    let userDocUnsubscribe: (() => void) | null = null;
    let timeoutId: ReturnType<typeof setTimeout> | null = null;

    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      // Clean up any existing user document listener
      if (userDocUnsubscribe) {
        userDocUnsubscribe();
        userDocUnsubscribe = null;
      }
      if (timeoutId) {
        clearTimeout(timeoutId);
        timeoutId = null;
      }

      if (firebaseUser) {
        // User is signed in - listen for their user document
        // This handles the race condition where the Cloud Function hasn't
        // created the user document yet after signup
        setState(prev => ({ ...prev, firebaseUser, loading: true, error: null }));

        let resolved = false;

        // Set a timeout to show an error if document never appears
        timeoutId = setTimeout(() => {
          if (!resolved) {
            resolved = true;
            if (userDocUnsubscribe) {
              userDocUnsubscribe();
              userDocUnsubscribe = null;
            }
            setState(prev => ({
              ...prev,
              loading: false,
              error: 'User profile not found. Please try again.',
              isSuperAdmin: false,
            }));
          }
        }, 15000); // 15 second timeout

        // Listen for user document (handles both existing docs and newly created ones)
        userDocUnsubscribe = onSnapshot(
          doc(db, 'users', firebaseUser.uid),
          async (snapshot) => {
            if (resolved) return;

            if (snapshot.exists()) {
              resolved = true;
              if (timeoutId) {
                clearTimeout(timeoutId);
                timeoutId = null;
              }

              const userData = snapshot.data() as User;

              // Fetch default organization
              const orgId = userData.defaultOrgId || userData.orgMemberships[0]?.orgId;
              let orgData: Organization | null = null;

              if (orgId) {
                try {
                  const orgDoc = await getDoc(doc(db, 'organizations', orgId));
                  if (orgDoc.exists()) {
                    orgData = orgDoc.data() as Organization;
                  }
                } catch (err) {
                  console.error('Error fetching organization:', err);
                }
              }

              // Set Sentry user context
              setSentryUser({ id: userData.id, email: userData.email });
              if (orgData) {
                setSentryOrganization({
                  id: orgData.id,
                  name: orgData.name,
                  planId: orgData.planId,
                });
              }

              // Compute effective super admin status
              // Requires both: email in _config/superAdmins AND @cleverbear.com domain
              const isSuperAdmin = await checkIsSuperAdmin(firebaseUser.email);

              setState({
                firebaseUser,
                user: userData,
                organization: orgData,
                loading: false,
                error: null,
                isSuperAdmin,
              });
            }
            // If document doesn't exist yet, keep waiting (listener will fire again when created)
          },
          (err) => {
            // Only show error if we haven't resolved yet
            if (!resolved) {
              resolved = true;
              if (timeoutId) {
                clearTimeout(timeoutId);
                timeoutId = null;
              }
              console.error('Error listening to user document:', err);
              captureException(err, { context: 'auth_state_change' });
              setState({
                firebaseUser,
                user: null,
                organization: null,
                loading: false,
                error: 'Failed to load user data.',
                isSuperAdmin: false,
              });
            }
          }
        );
      } else {
        // User is signed out - clear Sentry context
        clearSentryUser();
        setState({
          firebaseUser: null,
          user: null,
          organization: null,
          loading: false,
          error: null,
          isSuperAdmin: false,
        });
      }
    });

    return () => {
      unsubscribe();
      if (userDocUnsubscribe) {
        userDocUnsubscribe();
      }
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, []);

  // ---------------------------------------------------------------------------
  // Real-time User Data Listener
  // ---------------------------------------------------------------------------

  useEffect(() => {
    if (!state.firebaseUser) return;

    const unsubscribe = onSnapshot(
      doc(db, 'users', state.firebaseUser.uid),
      async (snapshot) => {
        if (snapshot.exists()) {
          const userData = snapshot.data() as User;
          // Recompute super admin status when user data changes
          // Requires both: email in _config/superAdmins AND @cleverbear.com domain
          const isSuperAdmin = await checkIsSuperAdmin(state.firebaseUser?.email);

          setState(prev => ({
            ...prev,
            user: userData,
            isSuperAdmin,
          }));
        }
      },
      (error) => {
        console.error('Error listening to user changes:', error);
      }
    );

    return () => unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.firebaseUser?.uid, state.firebaseUser?.email]);

  // ---------------------------------------------------------------------------
  // Auth Methods
  // ---------------------------------------------------------------------------

  const setError = (error: string | null) => {
    setState(prev => ({ ...prev, error }));
  };

  const clearError = useCallback(() => {
    setState(prev => ({ ...prev, error: null }));
  }, []);

  const signIn = useCallback(async (email: string, password: string) => {
    try {
      clearError();
      await signInWithEmailAndPassword(auth, email, password);
    } catch (err: unknown) {
      const error = err as { code?: string };
      if (error.code === 'auth/invalid-credential') {
        setError('Invalid email or password.');
      } else if (error.code === 'auth/too-many-requests') {
        setError('Too many failed attempts. Please try again later.');
      } else {
        setError('Failed to sign in. Please try again.');
      }
      throw err;
    }
  }, [clearError]);

  const signUp = useCallback(async (email: string, password: string) => {
    try {
      clearError();
      await createUserWithEmailAndPassword(auth, email, password);
    } catch (err: unknown) {
      const error = err as { code?: string };
      if (error.code === 'auth/email-already-in-use') {
        setError('An account with this email already exists.');
      } else if (error.code === 'auth/weak-password') {
        setError('Password must be at least 6 characters.');
      } else {
        setError('Failed to create account. Please try again.');
      }
      throw err;
    }
  }, [clearError]);

  const signInWithGoogle = useCallback(async () => {
    try {
      clearError();
      const provider = new GoogleAuthProvider();
      await signInWithPopup(auth, provider);
    } catch (err: unknown) {
      const error = err as { code?: string };
      if (error.code !== 'auth/popup-closed-by-user') {
        setError('Failed to sign in with Google.');
      }
      throw err;
    }
  }, [clearError]);

  const signInWithGithub = useCallback(async () => {
    try {
      clearError();
      const provider = new GithubAuthProvider();
      await signInWithPopup(auth, provider);
    } catch (err: unknown) {
      const error = err as { code?: string };
      if (error.code !== 'auth/popup-closed-by-user') {
        setError('Failed to sign in with GitHub.');
      }
      throw err;
    }
  }, [clearError]);

  const signOut = useCallback(async () => {
    try {
      await firebaseSignOut(auth);
    } catch (err) {
      setError('Failed to sign out.');
      throw err;
    }
  }, []);

  const resetPassword = useCallback(async (email: string) => {
    try {
      clearError();
      const requestPasswordReset = httpsCallable<{ email: string }, { success: boolean }>(
        functions,
        'requestPasswordReset'
      );
      await requestPasswordReset({ email });
      // Cloud Function always returns success to prevent email enumeration
    } catch (err) {
      // Still succeed silently to prevent email enumeration
      console.error('Password reset request failed:', err);
      // Don't set error or throw - silently succeed
    }
  }, [clearError]);

  const switchOrganization = useCallback(async (orgId: string) => {
    try {
      const orgDoc = await getDoc(doc(db, 'organizations', orgId));
      if (orgDoc.exists()) {
        const orgData = orgDoc.data() as Organization;
        // Update Sentry organization context
        setSentryOrganization({
          id: orgData.id,
          name: orgData.name,
          planId: orgData.planId,
        });
        setState((prev) => ({
          ...prev,
          organization: orgData,
        }));
      }
    } catch (err) {
      captureException(err, { context: 'switch_organization', orgId });
      setError('Failed to switch organization.');
      throw err;
    }
  }, []);

  const refreshOrganization = useCallback(async () => {
    const orgId = state.organization?.id;
    if (!orgId) return;

    try {
      const orgDoc = await getDoc(doc(db, 'organizations', orgId));
      if (orgDoc.exists()) {
        setState(prev => ({
          ...prev,
          organization: orgDoc.data() as Organization,
        }));
      }
    } catch (err) {
      console.error('Failed to refresh organization:', err);
    }
  }, [state.organization?.id]);

  const refreshAuth = useCallback(async () => {
    const currentUser = auth.currentUser;
    if (!currentUser) return;

    try {
      // Reload user data from Firebase Auth server
      // This updates emailVerified and other server-side changes
      await currentUser.reload();

      // Get the refreshed user and update state
      const refreshedUser = auth.currentUser;
      if (refreshedUser) {
        setState(prev => ({
          ...prev,
          firebaseUser: refreshedUser,
        }));
      }
    } catch (err) {
      console.error('Failed to refresh auth:', err);
    }
  }, []);

  // ---------------------------------------------------------------------------
  // Context Value
  // ---------------------------------------------------------------------------

  const value: AuthContextValue = {
    ...state,
    signIn,
    signUp,
    signInWithGoogle,
    signInWithGithub,
    signOut,
    resetPassword,
    switchOrganization,
    refreshOrganization,
    refreshAuth,
    clearError,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

// =============================================================================
// Hook
// =============================================================================

/**
 * Hook to access auth context.
 * Must be used within an AuthProvider.
 */
// eslint-disable-next-line react-refresh/only-export-components
export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  
  return context;
}
